/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Feature, Polygon, MultiPolygon } from 'geojson';

export enum DRAWMODE {
  POLYGON = 'POLYGON',
  BOX = 'BOX',
  MULTIPOINT = 'MULTIPOINT',
  POINT = 'POINT',
  LINESTRING = 'LINESTRING',
  SMOOTHLINE = 'SMOOTHLINE',
  CIRCLE = 'CIRCLE',
  SQUARE = 'SQUARE',
  OVAL = 'OVAL',
  LOCATION = 'LOCATION',
  FREEHANDLINE = 'FREEHANDLINE',
  ROTATABLEBOX = 'ROTATABLEBOX',
}

export interface FeatureEvent {
  coordinateIndexInFeature: number;
  featureIndex: number;
  screenCoords: { x: number; y: number };
  mouseX: number;
  mouseY: number;
  isInEditMode: boolean;
  feature: GeoJSON.Feature;
}

export type DrawModeExitCallback =
  | 'escaped'
  | 'doubleClicked'
  | 'clickedNearStartingPoint';

export type DrawModeValue = DRAWMODE | 'DELETE' | '';

type DefaultSelectionTypes = 'poly' | 'point' | 'box' | 'linestring';
// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
export type SelectionType = DefaultSelectionTypes | string;

export interface DrawMode {
  drawModeId: string;
  value: DrawModeValue;
  shape: GeoJSON.Feature | GeoJSON.FeatureCollection;
  title: string;
  isSelectable: boolean;
  selectionType: SelectionType;
}

export type ProjectFn = (
  feat: Feature<Polygon | MultiPolygon>,
) => Feature<Polygon | MultiPolygon>;
export type UnprojectFn = (
  feat: Feature<Polygon | MultiPolygon>,
) => Feature<Polygon | MultiPolygon>;
